import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { sendPlatformChatMessage } from '@/apis/chat';
import type { RootState } from '@/redux/store';
import { debug } from '@/utils/debug';
import {
  buildPlatformChatHistory,
  clearLegacyPlatformChatStorage,
  clearPlatformChatStorageForUser,
  EMPTY_MENTION_SCOPE,
  getPlatformChatStorageKey,
  isPatientScopeMismatch,
  loadPlatformChatSession,
  PLATFORM_CHAT_SESSION_STORAGE_KEY,
  savePlatformChatSession,
  type PlatformChatMention,
  type PlatformChatMentionScope,
} from '@/utils/platformChat';
import { getSelectedPlatformChatBedrockId } from '@/utils/platformChatModels';

export interface PlatformChatMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  isError?: boolean;
  mention?: PlatformChatMention | null;
}

interface PendingSend {
  text: string;
  fromComposer: boolean;
  scopeEpoch: number;
}

interface SessionSnapshot {
  userId: string | null;
  messages: PlatformChatMessage[];
  scope: PlatformChatMentionScope;
  isSending: boolean;
  draft: string;
  confirmedTurnCount: number | null;
  pendingSend: PendingSend | null;
}

const MENTION_RECONFIRM_EVERY = 5;

const loadSnapshot = (userId: string | null): SessionSnapshot => ({
  userId,
  ...loadPlatformChatSession<PlatformChatMessage>(userId),
  isSending: false,
  draft: '',
  confirmedTurnCount: null,
  pendingSend: null,
});

const useOwnedPlatformChatSession = () => {
  const userId = useSelector((state: RootState) => state.user.user_id) ?? null;
  const [state, setState] = useState<SessionSnapshot>(() => loadSnapshot(userId));
  const sessionRef = useRef(state);
  const scopeEpochRef = useRef(0);
  const messageSequenceRef = useRef(0);
  const mountedRef = useRef(true);
  let session = state;

  if (userId !== state.userId) {
    scopeEpochRef.current += 1;
    if (!userId && state.userId) clearPlatformChatStorageForUser(state.userId);
    session = loadSnapshot(userId);
    sessionRef.current = session;
    setState(session);
  }

  useEffect(() => {
    mountedRef.current = true;
    clearLegacyPlatformChatStorage();
    return () => {
      mountedRef.current = false;
      scopeEpochRef.current += 1;
    };
  }, []);

  useEffect(() => {
    savePlatformChatSession(session.userId, { messages: session.messages, scope: session.scope });
  }, [session.userId, session.messages, session.scope]);

  const publish = useCallback((next: SessionSnapshot) => {
    sessionRef.current = next;
    setState(next);
  }, []);

  const setDraft = useCallback(
    (draft: string) => {
      if (!userId || sessionRef.current.userId !== userId) return;
      publish({ ...sessionRef.current, draft });
    },
    [publish, userId]
  );

  const setActiveMention = useCallback(
    (mention: PlatformChatMention | null) => {
      if (!userId || sessionRef.current.userId !== userId) return;
      scopeEpochRef.current += 1;
      const snapshot = sessionRef.current;
      publish({
        ...snapshot,
        scope: { mention, boundary: snapshot.messages.length },
        isSending: false,
        draft: '',
        confirmedTurnCount: null,
        pendingSend: null,
      });
    },
    [publish, userId]
  );

  const clearActiveMention = useCallback(() => setActiveMention(null), [setActiveMention]);

  const send = useCallback(
    async (text: string) => {
      const snapshot = sessionRef.current;
      if (!text.trim() || snapshot.isSending || !userId || snapshot.userId !== userId) return;

      const sendUserId = snapshot.userId;
      const sendScopeEpoch = scopeEpochRef.current;
      const isCurrentScope = () =>
        mountedRef.current &&
        sessionRef.current.userId === sendUserId &&
        scopeEpochRef.current === sendScopeEpoch;
      const history = buildPlatformChatHistory(snapshot.messages.slice(snapshot.scope.boundary));
      const mentionedUserId = snapshot.scope.mention?.userProfileId;
      const modelId = getSelectedPlatformChatBedrockId();
      const userMsg: PlatformChatMessage = {
        id: `user-${Date.now()}-${++messageSequenceRef.current}`,
        role: 'user',
        content: text,
        created_at: new Date().toISOString(),
        mention: snapshot.scope.mention,
      };
      publish({ ...snapshot, messages: [...snapshot.messages, userMsg], isSending: true });

      try {
        const result = await sendPlatformChatMessage(text, history, {
          ...(mentionedUserId ? { mentionedUserId } : {}),
          ...(modelId ? { modelId } : {}),
        });
        if (!isCurrentScope()) return;
        const hasScopeMismatch = isPatientScopeMismatch(mentionedUserId, result.response.metadata);
        if (hasScopeMismatch) {
          debug.error('Platform chat patient scope mismatch; discarding response', {
            requested: mentionedUserId,
          });
        }
        const botMsg: PlatformChatMessage = {
          id: `bot-${Date.now()}-${++messageSequenceRef.current}`,
          role: 'bot',
          content: hasScopeMismatch
            ? 'This answer came back scoped to a different member than the one you asked about, so it was discarded. Re-select the member and try again.'
            : result.response.text,
          metadata: hasScopeMismatch ? undefined : result.response.metadata,
          created_at: new Date().toISOString(),
          isError: hasScopeMismatch || undefined,
        };
        const current = sessionRef.current;
        publish({ ...current, messages: [...current.messages, botMsg] });
      } catch (error) {
        if (!isCurrentScope()) return;
        debug.error('Platform chat send failed', error);
        const errMsg: PlatformChatMessage = {
          id: `err-${Date.now()}-${++messageSequenceRef.current}`,
          role: 'bot',
          content: 'Something went wrong. Please try again.',
          created_at: new Date().toISOString(),
          isError: true,
        };
        const current = sessionRef.current;
        publish({ ...current, messages: [...current.messages, errMsg] });
      } finally {
        if (isCurrentScope()) publish({ ...sessionRef.current, isSending: false });
      }
    },
    [publish, userId]
  );

  const userTurnsInScope = (snapshot: SessionSnapshot) =>
    snapshot.messages.slice(snapshot.scope.boundary).filter((message) => message.role === 'user')
      .length;

  const requestSend = useCallback(
    (text: string, fromComposer = false) => {
      const snapshot = sessionRef.current;
      const trimmed = text.trim();
      if (
        !trimmed ||
        !userId ||
        snapshot.userId !== userId ||
        snapshot.isSending ||
        snapshot.pendingSend
      )
        return;
      if (
        snapshot.scope.mention &&
        (snapshot.confirmedTurnCount === null ||
          userTurnsInScope(snapshot) >= snapshot.confirmedTurnCount + MENTION_RECONFIRM_EVERY)
      ) {
        publish({
          ...snapshot,
          pendingSend: { text: trimmed, fromComposer, scopeEpoch: scopeEpochRef.current },
        });
        return;
      }
      if (fromComposer) publish({ ...snapshot, draft: '' });
      void send(trimmed);
    },
    [publish, send, userId]
  );

  const pendingSend = session.pendingSend;
  const confirmPendingSend = useCallback(() => {
    const snapshot = sessionRef.current;
    if (
      !pendingSend ||
      snapshot.pendingSend !== pendingSend ||
      pendingSend.scopeEpoch !== scopeEpochRef.current ||
      snapshot.isSending ||
      !snapshot.userId
    ) {
      return;
    }
    publish({
      ...snapshot,
      pendingSend: null,
      confirmedTurnCount: userTurnsInScope(snapshot),
      draft: pendingSend.fromComposer ? '' : snapshot.draft,
    });
    void send(pendingSend.text);
  }, [pendingSend, publish, send]);

  const dismissPendingSend = useCallback(() => {
    if (!pendingSend || sessionRef.current.pendingSend !== pendingSend) return;
    publish({ ...sessionRef.current, pendingSend: null });
  }, [pendingSend, publish]);

  const changePendingMember = useCallback(() => {
    if (!pendingSend || sessionRef.current.pendingSend !== pendingSend) return;
    clearActiveMention();
    if (pendingSend.fromComposer) setDraft(pendingSend.text);
  }, [pendingSend, clearActiveMention, setDraft]);

  const clearMessages = useCallback(() => {
    if (sessionRef.current.userId !== userId) return;
    scopeEpochRef.current += 1;
    publish({
      userId: sessionRef.current.userId,
      messages: [],
      scope: EMPTY_MENTION_SCOPE,
      isSending: false,
      draft: '',
      confirmedTurnCount: null,
      pendingSend: null,
    });
  }, [publish, userId]);

  return {
    userId,
    storageKey: getPlatformChatStorageKey(PLATFORM_CHAT_SESSION_STORAGE_KEY, userId),
    messages: session.messages,
    activeMention: session.scope.mention,
    mentionBoundary: session.scope.boundary,
    setActiveMention,
    clearActiveMention,
    isSending: session.isSending,
    send,
    clearMessages,
    draft: session.draft,
    setDraft,
    pendingSend,
    isFirstConfirmInScope: session.confirmedTurnCount === null,
    requestSend,
    confirmPendingSend,
    changePendingMember,
    dismissPendingSend,
  };
};

const PlatformChatSessionContext = createContext<
  ReturnType<typeof useOwnedPlatformChatSession> | undefined
>(undefined);

export const PlatformChatSessionProvider = ({ children }: { children: ReactNode }) =>
  createElement(
    PlatformChatSessionContext.Provider,
    { value: useOwnedPlatformChatSession() },
    children
  );

export const usePlatformChatSession = () => {
  const session = useContext(PlatformChatSessionContext);
  if (!session) {
    throw new Error('usePlatformChatSession must be used within a PlatformChatSessionProvider');
  }
  return session;
};
